import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getWebSocketUrl } from '@/utils/websocket'

interface Reaction {
  id: string
  type: string
  emoji: string
  userId: string
  userName?: string
  timestamp: number
}

const reactionEmojis: { [key: string]: string } = {
  heart: '❤️',
  thumbsup: '👍',
  sparkles: '✨',
  flame: '🔥',
  party: '🎉',
}

export function useReactions(eventId: string) {
  const { user } = useAuth()
  const [reactions, setReactions] = useState<Reaction[]>([])
  const [reactionCounts, setReactionCounts] = useState<{ [key: string]: number }>({})
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    if (!eventId || !user) return

    // WebSocket接続
    const tokens = localStorage.getItem('auth_tokens')
    const accessToken = tokens ? JSON.parse(tokens).access_token : ''
    const wsUrl = getWebSocketUrl(`/api/v1/live/events/${eventId}/reactions?token=${accessToken}`)

    try {
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('Reactions WebSocket connected')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          // Backend sends: {reaction_type, user_id, user_name, timestamp}
          if (data.reaction_type) {
            const newReaction: Reaction = {
              id: `${data.user_id}-${data.timestamp}`,
              type: data.reaction_type,
              emoji: reactionEmojis[data.reaction_type] || '👍',
              userId: data.user_id,
              userName: data.user_name,
              timestamp: data.timestamp,
            }

            setReactions(prev => [...prev, newReaction])

            // カウント更新
            setReactionCounts(prev => ({
              ...prev,
              [data.reaction_type]: (prev[data.reaction_type] || 0) + 1,
            }))
          }
        } catch (error) {
          console.error('Failed to parse reaction message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('Reactions WebSocket error:', error)
      }

      ws.onclose = () => {
        console.log('Reactions WebSocket disconnected')
      }

      wsRef.current = ws
    } catch (error) {
      console.error('Failed to connect reactions WebSocket:', error)
    }

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close()
      }
    }
  }, [eventId, user])

  const sendReaction = useCallback((type: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !user) {
      console.warn('WebSocket not connected or user not authenticated')
      return
    }

    // Backend expects: {reaction_type: string}
    const message = {
      reaction_type: type,
    }

    wsRef.current.send(JSON.stringify(message))

    // ローカルで即座に反映（楽観的更新）
    const newReaction: Reaction = {
      id: `${user.id}-${Date.now()}`,
      type: type,
      emoji: reactionEmojis[type] || '👍',
      userId: user.id,
      userName: user.username,
      timestamp: Date.now(),
    }

    setReactions(prev => [...prev, newReaction])
    setReactionCounts(prev => ({
      ...prev,
      [type]: (prev[type] || 0) + 1,
    }))
  }, [eventId, user])

  const clearOldReactions = useCallback(() => {
    const now = Date.now()
    const timeout = 5000 // 5秒以上前のリアクションを削除

    setReactions(prev => prev.filter(r => now - r.timestamp < timeout))
  }, [])

  // 定期的に古いリアクションをクリア
  useEffect(() => {
    const interval = setInterval(clearOldReactions, 1000)
    return () => clearInterval(interval)
  }, [clearOldReactions])

  return {
    reactions,
    reactionCounts,
    sendReaction,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  }
}